import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom'; // For navigation
import { ArrowLeftIcon } from '../common/ArrowLeftIcon'; // Your arrow icon
import { toast } from 'react-toastify'; // Toast notifications

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);

  // Function to validate phone number format for any country, mirroring backend logic
  const isValidPhoneNumber = (number) => {
    if (!number) return false;
    let phone = number.trim();

    // If it starts with '+', treat it as an international number
    if (phone.startsWith('+')) {
      const cleaned = '+' + phone.slice(1).replace(/\D/g, ''); // Keep '+' and remove non-digits
      // Basic length validation for international numbers (e.g., +1234567890)
      // A typical international number is between 8 and 16 digits (excluding '+')
      if (cleaned.length >= 9 && cleaned.length <= 17) { // '+' and 8-16 digits
          return true;
      }
      return false; // Invalid length for an international number
    }

    // Handle local Ethiopian numbers (assuming they don't start with '+')
    let cleanedPhone = phone.replace(/\D/g, ''); // Remove all non-digits

    // Convert 09... or 07... to 2519... or 2517...
    if (/^09\d{8}$/.test(cleanedPhone) || /^07\d{8}$/.test(cleanedPhone)) {
      return true;
    }
    // Already in 251 format
    if (/^2519\d{8}$/.test(cleanedPhone) || /^2517\d{8}$/.test(cleanedPhone)) {
      return true;
    }

    // If it's a 9-digit number starting with 9 or 7 (e.g., 912345678 without leading 0), assume Ethiopian
    if (/^(9|7)\d{8}$/.test(cleanedPhone) && cleanedPhone.length === 9) {
      return true;
    }

    // For any other format, return false. This is a strict approach to avoid
    // storing ambiguous or incomplete phone numbers.
    return false;
  };

  // OTP cooldown countdown timer
  useEffect(() => {
    if (otpCooldown === 0) return;
    const timerId = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
    return () => clearTimeout(timerId);
  }, [otpCooldown]);

  // Handle sending OTP
  const handleSendOtp = async () => {
    const phone = phoneNumber.trim();

    if (!phone) {
      setErrorMessage('Please enter your phone number to send OTP.');
      toast.error('Please enter your phone number to send OTP.');
      return;
    }

    if (!isValidPhoneNumber(phone)) {
      setErrorMessage('Please enter a valid phone number (e.g., +1234567890 or 0912345678).');
      toast.error('Please enter a valid phone number (e.g., +1234567890 or 0912345678).');
      return;
    }

    if (otpCooldown > 0) {
      setErrorMessage(`Please wait ${otpCooldown} seconds before resending OTP.`);
      toast.warn(`Please wait ${otpCooldown} seconds before resending OTP.`);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const res = await axios.post('/api/auth/send-otp', {
        phoneNumber: phone,
        type: 'forgot',
      });

      if (res.data.success) {
        setOtpSent(true);
        setSuccessMessage('OTP sent successfully to your telegram.');
        toast.success('OTP sent successfully to your telegram.');
        setOtpCooldown(30);
      } else {
        setErrorMessage(res.data.message || 'Failed to send OTP.');
        toast.error(res.data.message || 'Failed to send OTP.');
      }
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message || err.message || 'Network or server error while sending OTP.'
      );
      toast.error(
        err.response?.data?.message || err.message || 'Network or server error while sending OTP.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset submission
  const handleResetPassword = async (e) => {
    e.preventDefault();
    const phone = phoneNumber.trim();
    const enteredOtp = otp.trim();
    const pass = newPassword;

    setErrorMessage('');
    setSuccessMessage('');

    if (!otpSent) {
      setErrorMessage('Please send OTP first.');
      toast.error('Please send OTP first.');
      return;
    }

    if (!phone || !enteredOtp || !pass) {
      setErrorMessage('Please fill in all fields.');
      toast.error('Please fill in all fields.');
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post('/api/auth/reset-password', {
        phoneNumber: phone,
        otp: enteredOtp,
        newPassword: pass,
      });

      if (res.data.success) {
        setSuccessMessage('Password reset successful! You can now log in.');
        toast.success('Password reset successful! You can now log in.');
        setOtpSent(false);
        setPhoneNumber('');
        setOtp('');
        setNewPassword('');
        setOtpCooldown(0);
        // Navigate to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setErrorMessage(res.data.message || 'Password reset failed.');
        toast.error(res.data.message || 'Password reset failed.');
      }
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message || err.message || 'Network or server error during password reset.'
      );
      toast.error(
        err.response?.data?.message || err.message || 'Network or server error during password reset.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-white rounded-tr-[6rem] shadow-lg overflow-hidden mx-auto mt-12">
      <div className="relative bg-[#1570EF] h-40 flex items-center justify-center p-6 rounded-tr-[6rem]">
        <button
          onClick={() => navigate('/login')}
          className="absolute top-4 left-4 text-white hover:text-gray-200"
          aria-label="Back to login"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h2 className="text-3xl font-bold text-white text-center">Forgot Password</h2>
      </div>
      <div className="p-8">
        <p className="text-center text-gray-600 mb-6">
          Enter your phone number to receive an OTP and reset your password.
        </p>

        {errorMessage && (
          <div
            aria-live="assertive"
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4"
            role="alert"
          >
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {errorMessage}</span>
          </div>
        )}
        {successMessage && (
          <div
            aria-live="assertive"
            className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4"
            role="alert"
          >
            <strong className="font-bold">Success!</strong>
            <span className="block sm:inline"> {successMessage}</span>
          </div>
        )}

        <form onSubmit={handleResetPassword}>
          <div className="mb-4">
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <div className="flex rounded-md shadow-sm">
              <input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  setErrorMessage('');
                  setSuccessMessage('');
                }}
                placeholder="e.g., +1234567890 or 0912345678"
                className="flex-1 px-4 py-3 rounded-l-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading || otpSent}
                autoComplete="tel"
              />
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={loading || otpCooldown > 0}
                className={`px-4 py-3 rounded-r-md border border-l-0 border-blue-500 text-white font-semibold transition duration-300 ${
                  loading || otpCooldown > 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#1570EF] hover:bg-[#155ed6]'
                }`}
              >
                {loading && !otpSent ? 'Sending...' : otpCooldown > 0 ? `Resend (${otpCooldown}s)` : 'Send OTP'}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
              OTP
            </label>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value);
                setErrorMessage('');
                setSuccessMessage('');
              }}
              placeholder="Enter OTP"
              className="w-full px-4 py-3 rounded-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading || !otpSent}
              autoComplete="one-time-code"
              required
            />
          </div>

          <div className="mb-6">
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setErrorMessage('');
                setSuccessMessage('');
              }}
              placeholder="Enter new password"
              className="w-full px-4 py-3 rounded-md border border-gray-300 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              autoComplete="new-password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full ${
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#1570EF] hover:bg-[#155ed6]'
            } text-white font-semibold py-3 rounded-md transition duration-300`}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
